import { msg } from "@lingui/core/macro";
import { For, Show } from "solid-js";
import { useNavigate, useSearch } from "@tanstack/solid-router";
import {
  AlertTriangle,
  Film,
  Filter,
  HardDrive,
  Loader2,
  RefreshCw,
  Search,
  Trash2,
} from "@fleet/icons";
import { Button } from "@/components/ui/button";
import { Badge, Card, CardContent, Skeleton } from "@fleet/ui";
import { Select, type SelectOption } from "@/components/ui/select";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import { stableRows } from "@/lib/solid/stable-rows";
import { adminQueryKeys, cn, type components, formatBytes } from "@space/shared";
import { showToast } from "@/components/ui/toast";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useI18n } from "@/providers/i18n-provider";

type ErrorModel = components["schemas"]["ErrorModel"];
type AdminVideoRow = components["schemas"]["AdminVideoRowDTO"];
type OrgUsageRow = components["schemas"]["OrgUsageRowDTO"];

const SKELETON_COUNT = 5;
type MediaFilter = "all" | "orphaned";

/** Badge color by video pipeline status. */
function statusClass(status: string): string {
  switch (status) {
    case "ready": {
      return "bg-success/10 text-success border-success/20";
    }
    case "failed": {
      return "bg-destructive/10 text-destructive border-destructive/20";
    }
    case "processing":
    case "uploading": {
      return "bg-amber-500/10 text-amber-600 border-amber-500/20";
    }
    default: {
      return "";
    }
  }
}

export function MediaManager() {
  const { t } = useI18n();
  // URL-addressable filter (#15, `?filter=`): pasting the URL reproduces the filter.
  const urlSearch = useSearch({ strict: false });
  const navigate = useNavigate();
  const filter = (): MediaFilter => (urlSearch().filter === "orphaned" ? "orphaned" : "all");
  const setFilter = (f: MediaFilter) => {
    void navigate({
      to: "/admin/media",
      search: (prev) => ({ ...prev, filter: f === "all" ? undefined : f }),
      replace: true,
    });
  };

  const usageQuery = createApiQuery<OrgUsageRow[], ErrorModel>(
    () => adminQueryKeys.mediaUsage(),
    async () => api.GET("/api/admin/media/usage", {}),
    () => ({ staleTime: 10_000 }),
  );

  const videosQuery = createApiQuery<AdminVideoRow[], ErrorModel>(
    () => adminQueryKeys.mediaVideos({ filter: filter() }),
    async () =>
      api.GET("/api/admin/media/videos", {
        params: { query: { filter: filter() } },
      }),
    () => ({ staleTime: 0 }),
  );

  const deleteMutation = createApiMutation<{ id: string }, unknown, ErrorModel>(async (vars) =>
    api.DELETE("/api/admin/media/videos/{id}", { params: { path: { id: vars.id } } }),
  );

  // Stable identity by id: every refetch re-emits new objects and <For>
  // would remount every row — killing an open delete ConfirmDialog
  // (same F4-D pattern as in authoring/catalog).
  const stableVideos = stableRows(() => videosQuery.data ?? undefined);
  const orphanCount = () => stableVideos().filter((v) => v.isOrphaned).length;

  const filterOptions = (): SelectOption<MediaFilter>[] => [
    { value: "all", label: t(msg`All videos`) },
    { value: "orphaned", label: t(msg`Orphaned only`) },
  ];

  const usageRows = () => usageQuery.data ?? [];

  return (
    <div class="space-y-8">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <Film class="h-8 w-8 text-primary" />
          <h2 class="text-3xl font-bold tracking-tight">{t(msg`Media library`)}</h2>
        </div>
        <Button
          variant="outline"
          onClick={() => {
            void usageQuery.refetch();
            void videosQuery.refetch();
          }}
          disabled={videosQuery.isLoading}
        >
          <RefreshCw class={cn("mr-2 h-4 w-4", videosQuery.isLoading && "animate-spin")} />
          {t(msg`Refresh`)}
        </Button>
      </div>

      {/* Storage usage per organization (bars vs the tier's quota). */}
      <div class="space-y-3">
        <div class="flex items-center gap-2">
          <HardDrive class="h-4 w-4 text-muted-foreground" />
          <h3 class="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            {t(msg`Storage usage by organization`)}
          </h3>
        </div>
        <Show when={!usageQuery.isLoading} fallback={<Skeleton class="h-24 w-full rounded-lg" />}>
          <div class="grid gap-3">
            <For
              each={usageRows()}
              fallback={
                <p class="text-sm text-muted-foreground py-4">{t(msg`No media stored yet.`)}</p>
              }
            >
              {(row) => {
                const pct = () =>
                  row.limitBytes > 0
                    ? Math.min(100, Math.round((row.usedBytes / row.limitBytes) * 100))
                    : 0;
                const over = () => row.limitBytes > 0 && row.usedBytes > row.limitBytes;
                const barColor = () => {
                  if (over()) {
                    return "bg-destructive";
                  }
                  return pct() > 80 ? "bg-amber-500" : "bg-primary";
                };
                return (
                  <Card>
                    <CardContent class="p-4 space-y-2">
                      <div class="flex items-center justify-between text-sm">
                        <span class="font-medium truncate">
                          {row.organizationName ?? row.organizationId ?? t(msg`Personal (no org)`)}
                        </span>
                        <span class="text-xs text-muted-foreground shrink-0">
                          {formatBytes(row.usedBytes)}
                          <Show when={row.limitBytes > 0}>
                            {" "}
                            / {formatBytes(row.limitBytes)}
                          </Show> ·{" "}
                          {row.videoCount} {t(msg`videos`)}
                        </span>
                      </div>
                      <Show when={row.limitBytes > 0}>
                        <div class="h-2 w-full rounded-full bg-muted overflow-hidden">
                          <div
                            class={cn("h-full rounded-full transition-all", barColor())}
                            style={{ width: `${pct()}%` }}
                          />
                        </div>
                      </Show>
                    </CardContent>
                  </Card>
                );
              }}
            </For>
          </div>
        </Show>
      </div>

      {/* Video listing with orphan filter + deletion. */}
      <div class="flex flex-col gap-4">
        <div class="flex items-center gap-2">
          <Filter class="h-4 w-4 shrink-0 text-muted-foreground" />
          <Select<MediaFilter>
            class="max-w-[220px]"
            aria-label={t(msg`Filter videos`)}
            value={filter()}
            onChange={setFilter}
            options={filterOptions()}
          />
          <Show when={orphanCount() > 0}>
            <Badge
              variant="outline"
              class="text-[10px] uppercase bg-amber-500/10 text-amber-600 border-amber-500/20"
            >
              {orphanCount()} {t(msg`orphaned`)}
            </Badge>
          </Show>
        </div>

        <Show
          when={!videosQuery.isLoading}
          fallback={
            <div class="grid gap-3">
              <For each={Array.from({ length: SKELETON_COUNT })}>
                {() => <Skeleton class="h-16 w-full rounded-lg" />}
              </For>
            </div>
          }
        >
          <div class="grid gap-3">
            <For
              each={stableVideos()}
              fallback={
                <div class="py-20 text-center text-muted-foreground">
                  <Search class="mx-auto h-12 w-12 opacity-10 mb-4" />
                  <p>{t(msg`No videos matching the filter.`)}</p>
                </div>
              }
            >
              {(video) => (
                <div
                  class={cn(
                    "flex flex-col gap-1 rounded-lg border p-4 transition-colors",
                    video.isOrphaned
                      ? "border-amber-500/20 bg-amber-500/5"
                      : "border-border hover:bg-muted/50",
                  )}
                >
                  <div class="flex items-center justify-between gap-2">
                    <div class="flex items-center gap-2 min-w-0">
                      <Show when={video.isOrphaned}>
                        <AlertTriangle class="h-4 w-4 text-amber-500 shrink-0" />
                      </Show>
                      <span class="font-medium text-sm truncate">{video.title}</span>
                    </div>
                    <div class="flex items-center gap-1 shrink-0">
                      <Badge
                        variant="outline"
                        class={cn("text-[9px] uppercase", statusClass(video.status))}
                      >
                        {video.status}
                      </Badge>
                      <Show when={video.isOrphaned}>
                        <Badge
                          variant="outline"
                          class="text-[9px] uppercase bg-amber-500/10 text-amber-600 border-amber-500/20"
                        >
                          {t(msg`Orphaned`)}
                        </Badge>
                      </Show>
                      <ConfirmDialog
                        title={t(msg`Delete this video?`)}
                        description={t(
                          msg`The video and its objects in R2 will be permanently removed. This cannot be undone.`,
                        )}
                        confirmLabel={t(msg`Delete`)}
                        variant="destructive"
                        onConfirm={() => {
                          deleteMutation.mutate(
                            { id: video.id },
                            {
                              onSuccess: () => {
                                showToast(t(msg`Video deleted.`), "success");
                                void videosQuery.refetch();
                                void usageQuery.refetch();
                              },
                              onError: () =>
                                showToast(t(msg`Could not delete the video.`), "error"),
                            },
                          );
                        }}
                        renderTrigger={(open) => (
                          <Button
                            variant="ghost"
                            size="icon"
                            class="h-7 w-7 text-destructive"
                            onClick={open}
                            disabled={deleteMutation.isPending}
                            aria-label={t(msg`Delete video`)}
                          >
                            <Show
                              when={deleteMutation.isPending}
                              fallback={<Trash2 class="h-4 w-4" />}
                            >
                              <Loader2 class="h-4 w-4 animate-spin" />
                            </Show>
                          </Button>
                        )}
                      />
                    </div>
                  </div>
                  <div class="flex items-center gap-3 text-[10px] text-muted-foreground flex-wrap">
                    <span>{formatBytes(video.sizeBytes)}</span>
                    <span>• {video.contentType}</span>
                    <Show when={video.organizationId}>
                      <span>
                        • {t(msg`org`)}: {video.organizationId}
                      </span>
                    </Show>
                    <Show when={video.courseId}>
                      <span class="text-success">
                        • {t(msg`course`)}: {video.courseId}
                      </span>
                    </Show>
                    <Show when={video.createdAt}>
                      {(createdAt) => <span>• {new Date(createdAt()).toLocaleDateString()}</span>}
                    </Show>
                  </div>
                </div>
              )}
            </For>
          </div>
        </Show>
      </div>
    </div>
  );
}
